import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Watch, Smartphone, Activity, RefreshCw, Check, X, Loader2, Footprints, Heart, Flame, Moon } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WearableConnect = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [connectedDevices, setConnectedDevices] = useState([]);
  const [connecting, setConnecting] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [wearableData, setWearableData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConnectedDevices();
    fetchWearableSummary();
  }, []);

  const fetchConnectedDevices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wearables/devices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnectedDevices(response.data.map(d => d.device_type));
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
    setLoading(false);
  };

  const fetchWearableSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wearables/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWearableData(response.data);
    } catch (error) {
      console.error('Error fetching wearable data:', error);
    }
  };

  const devices = [
    {
      id: 'google-fit',
      name: 'Google Fit',
      icon: Activity,
      color: 'bg-red-500',
      description: 'Sync steps, heart rate, and activity data',
      features: ['Steps', 'Heart Rate', 'Calories', 'Sleep']
    },
    {
      id: 'apple-health',
      name: 'Apple Health',
      icon: Smartphone,
      color: 'bg-pink-500',
      description: 'Connect your Apple Watch and iPhone health data',
      features: ['Blood Glucose', 'Heart Rate', 'Steps', 'Weight']
    },
    {
      id: 'fitbit',
      name: 'Fitbit',
      icon: Watch,
      color: 'bg-teal-500',
      description: 'Sync your Fitbit device data',
      features: ['Steps', 'Sleep', 'Heart Rate', 'SpO2']
    }
  ];

  const handleConnect = async (deviceId) => {
    setConnecting(deviceId);
    
    try {
      await axios.post(`${API_URL}/api/wearables/connect`, {
        device_id: `${deviceId}-${Date.now()}`,
        device_type: deviceId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setConnectedDevices([...connectedDevices, deviceId]);
      toast({
        title: t('wearables.connect', 'Device Connected'),
        description: `${devices.find(d => d.id === deviceId)?.name} ${t('wearables.connectedSuccess', 'has been connected successfully.')}`,
      });
      
      // Auto-sync after connecting
      await handleSync(deviceId);
    } catch (error) {
      toast({
        title: t('common.error', 'Error'),
        description: error.response?.data?.detail || t('wearables.connectError', 'Failed to connect device'),
        variant: "destructive"
      });
    }
    
    setConnecting(null);
  };

  const handleDisconnect = async (deviceId) => {
    try {
      await axios.delete(`${API_URL}/api/wearables/disconnect/${deviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setConnectedDevices(connectedDevices.filter(id => id !== deviceId));
      setWearableData(prev => prev ? { ...prev, today: { steps: 0, heart_rate: 0, calories_burned: 0, sleep: 0 } } : null);
      
      toast({
        title: t('wearables.disconnect', 'Device Disconnected'),
        description: `${devices.find(d => d.id === deviceId)?.name} ${t('wearables.disconnectedSuccess', 'has been disconnected.')}`,
      });
    } catch (error) {
      toast({
        title: t('common.error', 'Error'),
        description: t('wearables.disconnectError', 'Failed to disconnect device'),
        variant: "destructive"
      });
    }
  };

  const handleSync = async (deviceId) => {
    setSyncing(deviceId);
    
    try {
      const response = await axios.post(`${API_URL}/api/wearables/sync/${deviceId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: t('wearables.syncNow', 'Sync Complete'),
        description: `${response.data.data_points} ${t('wearables.dataPointsSynced', 'data points synced from')} ${devices.find(d => d.id === deviceId)?.name}`,
      });
      
      // Refresh summary data
      await fetchWearableSummary();
    } catch (error) {
      toast({
        title: t('common.error', 'Error'),
        description: t('wearables.syncError', 'Failed to sync device data'),
        variant: "destructive"
      });
    }
    
    setSyncing(null);
  };

  const isConnected = (deviceId) => connectedDevices.includes(deviceId);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Connected Devices</h2>
          <p className="text-gray-600 dark:text-gray-400">Sync data from your wearable devices</p>
        </div>
        <Badge variant="outline" className="text-emerald-600 border-emerald-600">
          {connectedDevices.length} Connected
        </Badge>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {devices.map((device) => {
          const Icon = device.icon;
          const connected = isConnected(device.id);
          const isConnecting = connecting === device.id;
          const isSyncing = syncing === device.id;

          return (
            <Card key={device.id} className={`dark:bg-gray-800 transition-all ${
              connected ? 'border-emerald-500 border-2' : ''
            }`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className={`w-12 h-12 ${device.color} rounded-xl flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  {connected && (
                    <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400">
                      <Check className="w-3 h-3 mr-1" />
                      Connected
                    </Badge>
                  )}
                </div>
                <CardTitle className="text-lg dark:text-white mt-3">{device.name}</CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-400">{device.description}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {device.features.map((feature) => (
                    <Badge key={feature} variant="secondary" className="text-xs">
                      {feature}
                    </Badge>
                  ))}
                </div>

                {connected ? (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleSync(device.id)}
                      disabled={isSyncing}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    >
                      {isSyncing ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Syncing...</>
                      ) : (
                        <><RefreshCw className="w-4 h-4 mr-2" /> Sync Now</>
                      )}
                    </Button>
                    <Button
                      onClick={() => handleDisconnect(device.id)}
                      variant="outline"
                      className="text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => handleConnect(device.id)}
                    disabled={isConnecting}
                    className="w-full"
                    variant="outline"
                  >
                    {isConnecting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...</>
                    ) : (
                      'Connect Device'
                    )}
                  </Button>
                )}

                {connected && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    Last synced: {new Date().toLocaleString()}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Today's Health Data Summary */}
      {wearableData && connectedDevices.length > 0 && (
        <Card className="bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 border-emerald-200 dark:border-emerald-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-600" />
              {t('wearables.todayHealth', "Today's Health Data")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                <Footprints className="w-6 h-6 mx-auto text-blue-500 mb-1" />
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {wearableData.today?.steps?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-gray-500">{t('wearables.steps', 'Steps')}</p>
              </div>
              <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                <Heart className="w-6 h-6 mx-auto text-red-500 mb-1" />
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {wearableData.today?.heart_rate || '--'}
                </p>
                <p className="text-xs text-gray-500">{t('wearables.heartRate', 'Heart Rate')} (bpm)</p>
              </div>
              <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                <Flame className="w-6 h-6 mx-auto text-orange-500 mb-1" />
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {wearableData.today?.calories_burned?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-gray-500">{t('wearables.calories', 'Calories')}</p>
              </div>
              <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                <Moon className="w-6 h-6 mx-auto text-indigo-500 mb-1" />
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {wearableData.today?.sleep || '--'}
                </p>
                <p className="text-xs text-gray-500">{t('wearables.sleepHours', 'Sleep')} (hrs)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardContent className="py-4">
          <div className="flex items-start space-x-3">
            <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-100">{t('wearables.whyConnect', 'Why Connect Devices?')}</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                {t('wearables.whyConnectDesc', 'Connecting your wearable devices allows DiaWise to import your health data automatically. This helps provide better insights and recommendations for managing your diabetes.')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WearableConnect;
